import { useCachedPermissionContext } from "./useCachedPermissionContext";
import { useMyPermissionsQuery } from "./usePermissionQueries";

/**
 * Check if the current user has a specific permission in a terreiro.
 * Uses the existing permission query infrastructure which calls
 * `/terreiros/:terreiroId/my-permissions` and returns string[] of codes.
 *
 * The backend returns `hasFullAccess: true` for super admins with
 * `admin.full_access`, and the `useMyPermissionsQuery` hook fetches
 * via `permissionRepository.getMyPermissions()` which returns permission codes.
 *
 * For super admins, the backend populates the `admin.full_access` code
 * in the permissions array, so we check for it explicitly.
 */
export const useHasPermission = (
  terreiroId?: string,
  permissionCode?: string,
  mediumId?: string,
): boolean => {
  const permissionContext = useCachedPermissionContext();
  const resolvedMediumId =
    mediumId ?? (!terreiroId ? permissionContext.mediumId : undefined);
  // A personal-scope check must not run while a terreiro is the active profile.
  const skipBecauseTerreiroIsActive =
    !terreiroId && Boolean(permissionContext.terreiroId);

  const { data: permissions } = useMyPermissionsQuery({
    terreiroId,
    mediumId: resolvedMediumId,
    enabled: !!permissionCode && !skipBecauseTerreiroIsActive,
  });

  if (!permissionCode || !permissions) return false;

  // Super admin has full access
  if (permissions.includes("admin.full_access")) return true;

  // Check specific permission code
  return permissions.includes(permissionCode);
};
